import { Debouncer } from "@tanstack/react-pacer";
import type { BrowserCommand, BrowserSessionSnapshot } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useRef } from "react";
import { resolveBrowserViewportSyncDecision } from "./BrowserPreviewPanel.logic";
import type { BrowserCommandRunOptions } from "./BrowserPreviewPanel.runCommand";

interface UseBrowserViewportSyncOptions {
  readonly hasBrowserSession: boolean;
  readonly runCommand: (
    command: BrowserCommand,
    options?: BrowserCommandRunOptions,
  ) => Promise<boolean>;
  readonly sessionViewport: BrowserSessionSnapshot["viewport"] | null | undefined;
  readonly setSurfaceViewport: (
    updater: (
      current: { readonly width: number; readonly height: number } | null,
    ) => { readonly width: number; readonly height: number } | null,
  ) => void;
}

export function useBrowserViewportSync(options: UseBrowserViewportSyncOptions) {
  const { hasBrowserSession, runCommand, sessionViewport, setSurfaceViewport } = options;
  const viewportSyncRef = useRef({
    hasBrowserSession,
    runCommand,
    sessionViewport: sessionViewport ?? null,
    setSurfaceViewport,
  });
  viewportSyncRef.current = {
    hasBrowserSession,
    runCommand,
    sessionViewport: sessionViewport ?? null,
    setSurfaceViewport,
  };

  const viewportSyncDebouncerRef = useRef(
    new Debouncer(
      async (size: { width: number; height: number }) => {
        const current = viewportSyncRef.current;
        const decision = resolveBrowserViewportSyncDecision({
          width: size.width,
          height: size.height,
          hasBrowserSession: current.hasBrowserSession,
          sessionViewport: current.sessionViewport,
        });
        const commitSurfaceViewport = () => {
          const { width, height } = decision.viewport;
          current.setSurfaceViewport((currentViewport) =>
            currentViewport?.width === width && currentViewport.height === height
              ? currentViewport
              : { width, height },
          );
        };
        if (decision.action === "ignore") {
          return;
        }
        if (decision.action === "commit") {
          commitSurfaceViewport();
          return;
        }
        try {
          const synced = await current.runCommand(
            {
              type: "setViewport",
              viewport: decision.viewport,
            },
            {
              reportError: false,
              visiblePending: false,
            },
          );
          if (synced) {
            commitSurfaceViewport();
          }
        } catch {
          // Viewport sync is best-effort.
        }
      },
      { wait: 300 },
    ),
  );

  useEffect(() => {
    const debouncer = viewportSyncDebouncerRef.current;
    return () => debouncer.cancel();
  }, []);

  return useCallback((size: { width: number; height: number }) => {
    viewportSyncDebouncerRef.current.maybeExecute(size);
  }, []);
}
